import { useEffect, useRef, useState } from "react";
import { ArrowUp, Copy, Eye, EyeOff, GripVertical, Trash2 } from "lucide-react";
import { useBuilder } from "./context";
import {
  DEVICE_WIDTHS, addMove, applyOverrides, ensureEids,
  findEditable, parentEidOf, setProp as setOvProp,
} from "./overrides";

export default function Canvas({ children }) {
  const b = useBuilder();
  const [hovered, setHovered] = useState(null);
  const ovBox = useRef(null);
  const hvBox = useRef(null);
  const tool = useRef(null);
  const ind = useRef(null);
  const dragEid = useRef(null);
  const dropInfo = useRef(null);

  const root = () => b.rootRef.current;
  const q = (eid) => root()?.querySelector(`[data-eid="${CSS.escape(eid)}"]`);

  useEffect(() => {
    const r = root();
    if (!r) return;
    ensureEids(r);
    r.dataset.editingEid = b.editing || "";
    applyOverrides(r, b.overrides, "builder");
    b.bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.remountKey, b.overrides, b.editing]);

  useEffect(() => {
    let raf;
    const loop = () => {
      const place = (node, eid) => {
        if (!node) return null;
        const el = eid ? q(eid) : null;
        if (!el || b.preview) {
          node.style.display = "none";
          return null;
        }
        const r = el.getBoundingClientRect();
        node.style.display = "block";
        node.style.transform = `translate(${r.left}px, ${r.top}px)`;
        node.style.width = `${r.width}px`;
        node.style.height = `${r.height}px`;
        return r;
      };
      const r = place(ovBox.current, b.selected);
      place(hvBox.current, hovered && hovered !== b.selected ? hovered : null);
      if (tool.current) {
        if (r && !b.editing) {
          tool.current.style.display = "flex";
          tool.current.style.transform = `translate(${r.left}px, ${Math.max(52, r.top - 42)}px)`;
        } else {
          tool.current.style.display = "none";
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  });

  const onMouseOver = (e) => {
    if (b.preview || b.editing) return;
    const el = findEditable(e.target, root());
    setHovered(el ? el.dataset.eid : null);
  };

  const onClickCapture = (e) => {
    if (b.preview || b.editing) return;
    const el = findEditable(e.target, root());
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    b.select(el.dataset.eid);
  };

  const onDblClick = (e) => {
    if (b.preview || b.editing) return;
    const el = findEditable(e.target, root());
    if (!el || !["text", "heading", "button", "link"].includes(el.dataset.etype)) return;
    e.preventDefault();
    e.stopPropagation();
    b.select(el.dataset.eid);
    b.setEditing(el.dataset.eid);
    el.contentEditable = "true";
    el.focus();
    const finish = () => {
      el.removeEventListener("blur", finish);
      el.contentEditable = "false";
      b.setEditing(null);
      b.commit(setOvProp(b.ovRef.current, el.dataset.eid, "text", el.innerText.trim()));
    };
    el.addEventListener("blur", finish);
  };

  const cleanupDrag = () => {
    const el = dragEid.current && q(dragEid.current);
    if (el) el.draggable = false;
    dragEid.current = null;
    dropInfo.current = null;
    if (ind.current) ind.current.style.display = "none";
  };

  const onDragStart = (e) => {
    const el = findEditable(e.target, root());
    if (!el) return;
    dragEid.current = el.dataset.eid;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", dragEid.current); } catch { /* noop */ }
  };

  const onDragOver = (e) => {
    if (!dragEid.current) return;
    e.preventDefault();
    const dragEl = q(dragEid.current);
    let t = findEditable(e.target, root());
    while (t && dragEl && (t === dragEl || dragEl.contains(t))) t = findEditable(t.parentElement, root());
    if (!t) {
      dropInfo.current = null;
      if (ind.current) ind.current.style.display = "none";
      return;
    }
    const r = t.getBoundingClientRect();
    const before = e.clientY < r.top + r.height / 2;
    if (ind.current) {
      ind.current.style.display = "block";
      ind.current.style.transform = `translate(${r.left}px, ${before ? r.top - 1 : r.bottom - 1}px)`;
      ind.current.style.width = `${r.width}px`;
    }
    dropInfo.current = { target: t.dataset.eid, before };
  };

  const onDrop = (e) => {
    e.preventDefault();
    const eid = dragEid.current;
    const info = dropInfo.current;
    cleanupDrag();
    if (!eid || !info) return;
    const el = q(eid);
    const t = q(info.target);
    if (!el || !t) return;
    const parent = t.parentElement;
    parent.insertBefore(el, info.before ? t : t.nextSibling);
    const sibs = Array.from(parent.querySelectorAll(":scope > [data-eid]"));
    b.commit(addMove(b.ovRef.current, eid, parentEidOf(parent), Math.max(0, sibs.indexOf(el))), true);
  };

  const selectParent = b.selectParent;
  const duplicate = b.duplicate;
  const toggleHide = b.toggleHide;
  const remove = b.removeSelected;

  useEffect(() => {
    const h = () => {
      const el = q(b.selected);
      if (!el || b.editing || b.preview) return;
      if (!["text", "heading", "button", "link"].includes(el.dataset.etype)) return;
      b.setEditing(el.dataset.eid);
      el.contentEditable = "true";
      el.focus();
      const finish = () => {
        el.removeEventListener("blur", finish);
        el.contentEditable = "false";
        b.setEditing(null);
        b.commit(setOvProp(b.ovRef.current, el.dataset.eid, "text", el.innerText.trim()));
      };
      el.addEventListener("blur", finish);
    };
    window.addEventListener("builder-inline-edit", h);
    return () => window.removeEventListener("builder-inline-edit", h);
  });

  const hiddenNow = !!b.overrides.elements?.[b.selected]?.hidden;
  const toolBtn = "flex h-7 w-7 items-center justify-center text-neutral-300 transition-colors hover:bg-white/10 hover:text-white";

  return (
    <div
      className="relative flex-1 overflow-y-auto bg-[#101012]"
      onMouseOver={onMouseOver}
      onClickCapture={onClickCapture}
      onDoubleClick={onDblClick}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={cleanupDrag}
      data-testid="builder-canvas"
    >
      <div
        ref={b.rootRef}
        data-vp={b.device}
        data-previewing={b.preview ? "1" : "0"}
        className="mx-auto min-h-full bg-[#0a0a0a] shadow-2xl transition-[width] duration-300"
        style={{ width: b.preview ? "100%" : DEVICE_WIDTHS[b.device], transform: "translateZ(0)" }}
      >
        {children}
      </div>

      <div ref={hvBox} style={{ display: "none" }} data-builder-ui className="pointer-events-none fixed left-0 top-0 z-[120] border border-dashed border-sky-400/80" data-testid="hover-outline" />
      <div ref={ovBox} style={{ display: "none" }} data-builder-ui className="pointer-events-none fixed left-0 top-0 z-[121] border-2 border-sky-500" data-testid="selection-outline" />
      <div ref={ind} style={{ display: "none" }} data-builder-ui className="pointer-events-none fixed left-0 top-0 z-[122] h-0.5 bg-sky-400" data-testid="drop-indicator" />

      <div ref={tool} style={{ display: "none" }} data-builder-ui className="fixed left-0 top-0 z-[130] items-center gap-0.5 border border-white/10 bg-[#1a1a1d] p-1 shadow-xl" data-testid="selection-toolbar">
        <button onClick={selectParent} className={toolBtn} title="Select parent" data-testid="tool-select-parent"><ArrowUp className="h-3.5 w-3.5" /></button>
        <button onMouseDown={() => { const el = q(b.selected); if (el) el.draggable = true; }} className={`${toolBtn} cursor-grab`} title="Drag to move" data-testid="tool-drag"><GripVertical className="h-3.5 w-3.5" /></button>
        <button onClick={duplicate} className={toolBtn} title="Duplicate" data-testid="tool-duplicate"><Copy className="h-3.5 w-3.5" /></button>
        <button onClick={toggleHide} className={toolBtn} title={hiddenNow ? "Show" : "Hide"} data-testid="tool-hide">{hiddenNow ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
        <button onClick={remove} className={`${toolBtn} hover:text-red-400`} title="Delete" data-testid="tool-delete"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}
